import {
  completeOnboardingTaskReq,
  getOnboardingTasksReq,
  OnboardingTask,
  OnboardingTaskName,
} from '@/components/userguide/utils';
import { createContext, FC, PropsWithChildren, useCallback, useContext, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { axiosController } from '@/lib/network';
import { Result } from '@/lib/type';
import { getWorkGroupDetail } from '@/pages/workgroup/api';
import { useAuth0 } from '@auth0/auth0-react';
import { useOrgContext } from '@/contexts/orgContext';
import { isTermsAccepted } from '@/components/landing/LegalTerms';
import { WorkspaceT } from '@/pages/workgroup/type';
import { isOrgAdmin } from '@/pages/admin/user/type';

interface UserGuideContextValue {
  onboardingTasks: OnboardingTask[];
  isFetching: boolean;
  completeOnboardingTask: (name: string) => Promise<void>;
  allFinished: boolean;
  isAcquiringWorkgroup: boolean;
  acquiredWorkspace: WorkspaceT | undefined;
  clearAcquiredWorkgroup: () => void;
  workspaceError: string | null;
  determineNeedWorkspaceMutation: ReturnType<
    typeof useMutation<boolean, unknown, { acceptedTos?: boolean; marketingDone?: boolean }, unknown>
  >;
  startAcquireWorkgroup: () => Promise<void>;
}

const UserGuideContext = createContext<UserGuideContextValue>({} as UserGuideContextValue);

const useUserGuideContext = () => {
  const context = useContext(UserGuideContext);
  return context;
};

const UserGuideProvider: FC<PropsWithChildren> = (props) => {
  const queryClient = useQueryClient();
  const { data: onboardingTasks = [], isFetching } = useQuery('onboarding-tasks', getOnboardingTasksReq);

  const [isAcquiringWorkgroup, setIsAcquiringWorkgroup] = useState(false);
  const [acquiredWorkspace, setAcquiredWorkspace] = useState<WorkspaceT>();
  const [workspaceError, setWorkspaceError] = useState<string | null>(null);

  const { getIdTokenClaims } = useAuth0();
  const { rawOrg, userInfo } = useOrgContext();

  const acquireWorkgroup = useCallback(async () => {
    try {
      setIsAcquiringWorkgroup(true);
      setWorkspaceError(null);
      const response = await axiosController.get<Result<{ workgroup_id: string }>>('v2/pooled-workgroups/acquire');
      if (response.status === 200) {
        const workgroupId = response.data.Result?.workgroup_id;
        if (!workgroupId) {
          throw new Error('Failed to acquire workspace.');
        }

        // for production, we use 60 seconds, for development, we use 10 minutes
        const pollTimeout =
          import.meta.env.MODE === 'production' || import.meta.env.MODE === 'staging' ? 60000 : 600000;
        const pollInterval = 1000; // 1 second
        const startTime = Date.now();
        let workgroupReady = false;
        while (Date.now() - startTime < pollTimeout) {
          try {
            const workgroupResponse = await getWorkGroupDetail(workgroupId);
            if (workgroupResponse.Result?.workspaces[0]?.status === 'Active') {
              workgroupReady = true;
              setAcquiredWorkspace(workgroupResponse.Result?.workspaces[0]);
              break;
            }
          } catch (error) {
            // ignore error and continue polling
          }
          await new Promise((resolve) => setTimeout(resolve, pollInterval));
        }

        queryClient.invalidateQueries(['user-credits']);
        queryClient.invalidateQueries(['onboarding-tasks']);
        if (!workgroupReady) {
          console.log('workspace did not become active in time.');
          throw new Error('workspace did not become active in time.');
        }
      } else {
        throw new Error('Failed to acquire workspace.');
      }
    } catch (error) {
      console.log('acquire workspace error', error);
      setWorkspaceError((error as Error).message);
    } finally {
      setIsAcquiringWorkgroup(false);
    }
  }, [queryClient]);

  const clearAcquiredWorkgroup = useCallback(() => {
    console.log('clearAcquiredWorkgroup');

    setAcquiredWorkspace(undefined);
    setIsAcquiringWorkgroup(false);
    setWorkspaceError(null);
  }, []);

  // Pure determination: decide whether we should acquire a workgroup, without side effects
  const determineNeedWorkspace = useCallback(
    async ({ acceptedTos, marketingDone }: { acceptedTos?: boolean; marketingDone?: boolean }) => {
      const idToken = await getIdTokenClaims();
      acceptedTos = acceptedTos ?? isTermsAccepted(idToken);
      marketingDone = marketingDone ?? !!rawOrg?.marketing_done;
      const createWorkspaceTask = onboardingTasks.find((task) => task.task_name === OnboardingTaskName.createWorkspace);
      const orgAdmin = isOrgAdmin(userInfo.roles);
      console.log('acceptedTos', acceptedTos);
      console.log('marketingDone', marketingDone);
      console.log('orgAdmin', orgAdmin);
      console.log('createWorkspaceTask', createWorkspaceTask);
      if (acceptedTos && marketingDone && orgAdmin && (!createWorkspaceTask || !createWorkspaceTask.completed)) {
        return true;
      } else {
        return false;
      }
    },
    [getIdTokenClaims, rawOrg, onboardingTasks, userInfo]
  );

  const determineNeedWorkspaceMutation = useMutation(determineNeedWorkspace);

  const startAcquireWorkgroup = useCallback(async () => {
    await acquireWorkgroup();
  }, [acquireWorkgroup]);

  const completeOnboardingTask = useCallback(
    async (name: string) => {
      const task = onboardingTasks.find((task) => task.task_name === name);
      if (task && !task.completed) {
        await completeOnboardingTaskReq(task.id);
        task.completed = true;
        queryClient.setQueryData(
          'onboarding-tasks',
          onboardingTasks.map((t) => (t.task_name === name ? task : t))
        );
        queryClient.invalidateQueries(['user-credits']);
        queryClient.invalidateQueries(['onboarding-tasks']);
      }
    },
    [onboardingTasks, queryClient]
  );

  const allFinished = onboardingTasks.length > 0 && onboardingTasks.every((t) => t.completed);

  const UserGuideContextValue: UserGuideContextValue = {
    onboardingTasks,
    isFetching,
    completeOnboardingTask,
    allFinished,
    isAcquiringWorkgroup,
    acquiredWorkspace,
    clearAcquiredWorkgroup,
    workspaceError,
    determineNeedWorkspaceMutation,
    startAcquireWorkgroup,
  };

  return <UserGuideContext.Provider value={UserGuideContextValue} {...props} />;
};

export { UserGuideProvider, useUserGuideContext };
